import { SettingsApplications } from '@mui/icons-material';
import { Alert, Button, MenuItem } from '@mui/material';
import _ from 'lodash';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { MdAdd } from 'react-icons/md';
import { useAdminData } from '../../../../../hooks/useAdminData';
import { LiveStream, Meetingroom } from '../../../../../interfaces/interfaces';
import { StageComponentInterface } from '../../../../../interfaces/pagebuilder';
import { fetchAndSetAdminEvent } from '../../../../../reduxToolkit/slices/adminSlice';
import { fetchAndSetEvent, setLiveStreams } from '../../../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../../../reduxToolkit/typedRedux';
import { getLiveStreams } from '../../../../../services/services';
import LivestreamModalCreate from '../../../../account/Livestream/LivestreamModalCreate';
import LivestreamModalUpdate from '../../../../account/Livestream/LivestreamModalUpdate';
import VySelect from '../../../../common/VySelect';
import VyTooltip from '../../../../common/VyTooltip';
import VyUpload from '../../../../common/VyUpload';
import { PageBuilderSettingsSection, PageBuilderSettingsTable } from '../PageBuilderSettingsTable';
import { CreateStudioModal } from './studio/CreateStudioModal';
import { StudioSelect } from './studio/StudioSelect';
import { SupportedFunctions, useEventFunctions } from '../../../../../hooks/useEventFunctions';

export interface StageSettingsTableInterface {
  component: StageComponentInterface;
  onChange: (c: StageComponentInterface) => void;
}

const StageSettingsTable = ({ component, onChange }: StageSettingsTableInterface) => {
  const { t } = useTranslation(['pagebuilder']);
  const [showLivestreamModal, setShowLivestreamModal] = useState(false);

  const { isFunctionEnabled } = useEventFunctions();

  const livestreams = useSelector((state) => state.common.livestreams);
  const event = useSelector((state) => state.common.event);

  const { updateEventProperty } = useAdminData(false);
  const dispatch = useDispatch();

  const [showStudioCreateModal, setShowStudioCreateModal] = useState(false);

  /**
   * We need the stream type to check if it can be protected or not
   * Dynamically changing the display of checkboxes is thus easier
   */
  //const [streamType, setStreamType] = useState(component?.stageSettings?.streamType);
  const [currentLivestream, setCurrentLivestream] = useState<LiveStream>();
  const [openLivestreamModal, setOpenLivestreamModal] = useState(false);

  useEffect(() => {
    const current = livestreams.find((l) => l.hls === component.stageSettings?.livestream);
    setCurrentLivestream(current);
  }, [component, livestreams]);

  function onSubmit(props: any) {
    const _props: any = { ...props };
    const c = {
      ...component,
      ..._props,
      stageSettings: {
        ...component.stageSettings,
        ..._props.stageSettings,
      },
    };
    onChange(c);
  }

  function updateSelectedLivestream(livestreamHls: string, livestreamList?: LiveStream[]) {
    const hls = livestreamHls;
    const _component = _.cloneDeep(component);
    _.set(_component, 'stageSettings.livestream', hls);

    /**
     * Stream workaround for backward comp.
     */
    const streams = livestreamList ?? livestreams;
    const s = streams.find((stream) => stream.hls === hls);
    if (s) {
      _.set(_component, 'stageSettings.vystemstream', s);
    }
    onChange(_component);
  }

  async function fetchLiveStreams() {
    if (!event) return;
    const ls = await getLiveStreams(event._id);
    dispatch(setLiveStreams(ls));
    if (currentLivestream) updateSelectedLivestream(currentLivestream?.hls, ls);
  }

  useEffect(() => {
    dispatch(fetchAndSetAdminEvent({ eventid: event!._id }));
  }, []);

  const settingSections: PageBuilderSettingsSection[] = [
    {
      name: t('pagebuilder:stream-settings'),
      actions:
        component?.stageSettings?.streamType === 'Vystemstream'
          ? [
              {
                icon: (
                  <VyTooltip title={t('account:livestream.add')}>
                    <MdAdd />
                  </VyTooltip>
                ),
                onClick: () => {
                  setShowLivestreamModal(!showLivestreamModal);
                },
              },
            ]
          : component?.stageSettings?.streamType === 'vystemStudio'
          ? [
              {
                icon: (
                  <VyTooltip title={t('account:studio.add')}>
                    <MdAdd />
                  </VyTooltip>
                ),
                onClick: () => {
                  setShowStudioCreateModal(true);
                },
              },
            ]
          : [],
      settings: [
        {
          label: t('account:stream-provider'),
          content: (
            <VySelect
              fullWidth
              variant="outlined"
              value={_.get(component, 'stageSettings.streamType')}
              onChange={(e: any) => {
                const _component = _.cloneDeep(component);
                _.set(_component, 'stageSettings.streamUrl', null);
                _.set(_component, 'stageSettings.thumbnail', null);
                _.set(_component, 'stageSettings.streamType', e.target.value);
                onChange(_component);
              }}
              MenuProps={{
                style: { zIndex: 1320 },
              }}>
              {isFunctionEnabled(SupportedFunctions.livestreams, event) && <MenuItem value="Vystemstream">vystem Livestream</MenuItem>}
              <MenuItem value="Youtube">YouTube</MenuItem>
              <MenuItem value="Vimeo">Vimeo</MenuItem>
              {event?.modules?.studios?.active && <MenuItem value="vystemStudio">vystem Studio</MenuItem>}
              <MenuItem value="iFrame">iFrame</MenuItem>
              <MenuItem value="videourl">Video Url</MenuItem>
              <MenuItem value="media">{t('account:media')}</MenuItem>
            </VySelect>
          ),
        },
        {
          visible: component?.stageSettings?.streamType === 'iFrame',
          label: 'iFrame',
          type: 'text',
          path: 'stageSettings.iFrame',
        },
        {
          visible: component?.stageSettings?.streamType === 'Vystemstream',
          label: t('account:stream-select'),
          content: (
            <div className="flex flex-row space-x-2 w-full">
              {livestreams.length > 0 ? (
                <VySelect
                  fullWidth
                  variant="outlined"
                  value={_.get(component, 'stageSettings.livestream')}
                  onChange={(e: any) => updateSelectedLivestream(e.target.value)}
                  MenuProps={{
                    style: { zIndex: 1320 },
                  }}>
                  {livestreams
                    .filter((stream) => stream.isStudioLiveStream !== true)
                    .map((livestream) => (
                      <MenuItem value={livestream.hls}>{livestream.name}</MenuItem>
                    ))}
                </VySelect>
              ) : (
                <div className="space-y-2 w-full">
                  <Alert severity="info">{t('pagebuilder:no-livestream-info')}</Alert>
                </div>
              )}
              <LivestreamModalCreate
                open={showLivestreamModal}
                eventid={event?._id}
                onSubmit={fetchLiveStreams}
                onClose={() => setShowLivestreamModal(false)}
              />
              {currentLivestream && (
                <VyTooltip title={t('pagebuilder:stage-open-livestream-modal')}>
                  <Button variant="outlined" className="h-full" onClick={() => setOpenLivestreamModal(true)}>
                    <SettingsApplications />
                  </Button>
                </VyTooltip>
              )}{' '}
            </div>
          ),
        },
        {
          visible: component?.stageSettings?.streamType === 'vystemStudio',
          label: t('account:studio.configuration-title'),
          content: <StudioSelect onChange={onChange} component={component} />,
        },
        {
          label: t('account:stream-url'),
          visible: component?.stageSettings?.streamType && ['Youtube', 'Vimeo', 'videourl'].includes(component?.stageSettings.streamType),
          type: 'text',
          path: 'stageSettings.streamUrl',
        },
        {
          visible: component?.stageSettings?.streamType === 'media',
          label: t('account:stream-select'),
          content: (
            <VyUpload
              hideUpload
              randomExternalImageButton={false}
              acceptedFileTypes="video/*"
              showFileUploadProgress
              getFileObject
              showPreview={false}
              onChange={(data, file, asset, thumbnail) => {
                if (!asset?.playback_ids || asset.playback_ids.length === 0) return;
                const _component = _.cloneDeep(component);
                _.set(_component, 'stageSettings.streamUrl', `https://stream.mux.com/${asset.playback_ids?.[0]?.id}.m3u8`);
                _.set(_component, 'stageSettings.thumbnail', thumbnail);
                onChange(_component);
              }}
              allowMuxVideos
            />
          ),
        },
      ],
    },
    {
      name: t('pagebuilder:player-settings'),
      settings: [
        {
          visible: component?.stageSettings?.streamType === 'Vystemstream',
          label: t('account:is-pippable'),
          tooltip: t('account:tooltip-is-pippable'),
          type: 'boolean',
          path: 'stageSettings.isPippable',
        },
        {
          label: t('pagebuilder:is-loop-video'),
          visible: component?.stageSettings?.streamType && ['Youtube', 'Vimeo', 'videourl', 'media'].includes(component?.stageSettings.streamType),
          type: 'boolean',
          path: 'stageSettings.isLoopVideo',
        },
        {
          label: t('account:stage-livestream-autoplay'),
          tooltip: t('account:stage-livestream-autoplay-warning'),
          path: 'stageSettings.autoplay',
          type: 'boolean',
        },
      ],
    },
    {
      name: t('pagebuilder:select-functions'),
      settings: [
        {
          visible: component.theme !== 'mbw' && isFunctionEnabled(SupportedFunctions.qacontrol, event),
          label: t('account:qa-function'),
          path: 'stageSettings.qa.active',
          type: 'boolean',
        },
        {
          label: t('config:privateModeQA-title'),
          tooltip: t('config:privateModeQA-description'),
          defaultValue: event?.settings?.privateModeQA,
          disabled: !event,
          visible: component?.stageSettings?.qa.active,
          type: 'boolean',
          onChange: async () => {
            await updateEventProperty(!event?.settings?.privateModeQA, 'settings.privateModeQA');
            dispatch(fetchAndSetEvent({ eventid: event!._id }));
          },
        },
        {
          visible: component.stageSettings?.qa?.active && component.theme !== 'mbw',
          label: t('pagebuilder:qa-settings.show-public-questions'),
          type: 'boolean',
          path: 'stageSettings.qa.settings.showQuestionsPublic',
        },
        {
          visible: component.stageSettings?.qa?.active && component.theme !== 'mbw',
          tooltip: `${t('pagebuilder:qa-settings.tooltip-max-length')} (${t('account:qa-function')})`,
          label: `${t('pagebuilder:qa-settings.max-length')} (${t('account:qa-function')})`,
          type: 'number',
          conditions: {
            max: { value: 10000, message: t('config:errors.maximum-value', { value: 10000 }) },
            min: { value: 200, message: t('config:errors.minimum-value', { value: 200 }) },
          },
          path: 'stageSettings.qa.settings.maxLength',
          defaultValue: component.stageSettings?.qa?.settings?.maxLength ?? 2000,
        },
        {
          visible: component.theme !== 'mbw',
          label: t('account:chat-function'),
          type: 'boolean',
          path: 'stageSettings.chat.active',
        },
        {
          visible: component.theme === 'mbw',
          label: t('account:speakers-function'),
          type: 'boolean',
          path: 'stageSettings.speakers.active',
        },
        {
          visible: component.theme === 'mbw',
          label: t('account:agenda-function'),
          type: 'boolean',
          path: 'stageSettings.agenda.active',
        },
        {
          visible: component.theme === 'mbw',
          label: t('account:downloads-function'),
          type: 'boolean',
          path: 'stageSettings.downloads.active',
        },
        {
          label: t('account:like-button'),
          type: 'boolean',
          path: 'stageSettings.like.active',
        },
        {
          label: t('account:poll-function'),
          visible: isFunctionEnabled(SupportedFunctions.polls, event),
          type: 'boolean',
          path: 'stageSettings.poll.active',
        },
      ],
    },
  ];

  return (
    <>
      {currentLivestream && openLivestreamModal && (
        <LivestreamModalUpdate
          livestream={currentLivestream}
          open={!!currentLivestream && openLivestreamModal}
          eventid={event?._id}
          onSubmit={() => fetchLiveStreams()}
          onClose={() => setOpenLivestreamModal(false)}
          onUpdate={() => fetchLiveStreams()}
        />
      )}
      <CreateStudioModal
        open={showStudioCreateModal}
        onClose={() => setShowStudioCreateModal(false)}
        onSuccess={(studio: Meetingroom) => {
          const _component = _.cloneDeep(component);
          _.set(_component, 'stageSettings.vystemStudio', studio);
          onChange(_component);

          setShowStudioCreateModal(false);
        }}
      />
      <PageBuilderSettingsTable sections={settingSections} onChange={onChange} component={component} />
    </>
  );
};
export default StageSettingsTable;
